import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import StatCard from '../../components/StatCard.jsx';
import StatusBadge from '../../components/StatusBadge.jsx';
import PieChart from '../../components/PieChart.jsx';
import DashboardCard from '../../components/DashboardCard.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { AvatarGroup } from '../../components/Avatar.jsx';
import { TasksAPI } from '../../lib/api';
import { countNounLabel, pendingTaskCount } from '../../lib/pendingCounts';
import { taskCardDate, truncateTaskTitle } from '../../lib/taskDisplay';
import { useAuth } from '../../lib/auth.jsx';
import { buildPath, statusMeta, isIndividualUser } from '../../config';


/** Tasks home — mirrors templates/task_new/index.html. */
export default function TasksDashboard() {
  const { user } = useAuth();
  const solo = isIndividualUser(user);

  const [summary, setSummary] = useState(null);
  const [teamSummary, setTeamSummary] = useState(null);
  const [myTasks, setMyTasks] = useState([]);
  const [teamTasks, setTeamTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const requests = [
          TasksAPI.summary({ mine: 1 }),
          TasksAPI.list({ mine: 1, exclude_status: 'completed', page_size: 5 }),
        ];
        if (!solo) {
          requests.push(
            TasksAPI.list({ exclude_mine: 1, exclude_status: 'completed', page_size: 5 }),
          );
          requests.push(TasksAPI.summary({ exclude_mine: 1 }));
        }
        const [sum, mine, team, teamSum] = await Promise.all(requests);
        if (cancelled) return;
        setSummary(sum);
        setMyTasks(mine.results || mine);
        if (team) setTeamTasks(team.results || team);
        if (teamSum) setTeamSummary(teamSum);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [solo]);


  if (loading) return <Loader label="Loading dashboard…" />;

  const counts = (summary && summary.by_status) || {};
  const total = (summary && summary.total) || 0;
  const minePending = pendingTaskCount(summary);
  const teamPending = pendingTaskCount(teamSummary);
  const minePendingLabel = countNounLabel(minePending, 'pending task');
  const teamPendingLabel = countNounLabel(teamPending, 'pending task');

  return (
    <>
      <PageHeader
        title="Task Dashboard"
        actions={
          <>
            <Link className="ty-btn ty-btn--ghost" to={buildPath.routineTasks()}>
              Routine Tasks
            </Link>{' '}
            <Link className="ty-btn" to="/task/create-task">+ Create Task</Link>
          </>
        }
      />

      <ErrorBox error={error} />

      {/* Stat tile colours mirror the Taskose `.task-status.type-N`
          palette so the React dashboard matches the original HTML theme.

          Every tile links into the task list.  The per-status tiles carry
          ``?status=…`` so the list opens already narrowed to the rows the
          number counted (``TasksList`` reads the status from the query
          string for exactly this reason) — otherwise a user who clicks
          "Awaiting Feedback: 7" would land on the full list and have to
          re-pick the status by hand.  The summary is fetched with
          ``mine: 1``, so the tiles point at the matching ``my_tasks``
          filter and the numbers agree with the page they open.

          The count sits at the top-right of each tile (see ``StatCard``)
          so the pending / status numbers are readable at a glance. */}
      <div className="ty-stats">
        <StatCard label="Total Tasks"       value={total}                            accent="#7980ff"
                  to={buildPath.tasksList('my_tasks')} />
        <StatCard label="To Do"             value={counts.assigned || 0}             accent="#ec4899"
                  to={`${buildPath.tasksList('my_tasks')}?status=assigned`} />
        <StatCard label="In Progress"       value={counts.in_progress || 0}          accent="#34c8de"
                  to={`${buildPath.tasksList('my_tasks')}?status=in_progress`} />
        <StatCard label="Awaiting Feedback" value={counts.waiting_for_feedback || 0} accent="#f9771d"
                  to={`${buildPath.tasksList('my_tasks')}?status=waiting_for_feedback`} />
        <StatCard label="Completed"         value={counts.completed || 0}            accent="#5fd788"
                  to={`${buildPath.tasksList('my_tasks')}?status=completed`} />
      </div>

      <div className="ty-grid-2">
        <TaskList
          title="Tasks Assigned To Me"
          tasks={myTasks}
          filter="my_tasks"
          pending={minePending}
          pendingLabel={minePendingLabel}
        />
        {!solo && (
        <TaskList
          title="Tasks Assigned To My Team"
          tasks={teamTasks}
          filter="my_team_tasks"
          pending={teamPending}
          pendingLabel={teamPendingLabel}
        />
        )}
      </div>

      {/* Donut chart of my tasks by status — equivalent of the
          `Highcharts.chart('my_task_container', { type: 'pie' })` widget
          in templates/task_new/index.html. */}
      <div className="ty-grid-2 ty-mt-3">
        <DashboardCard
          title="My Tasks Chart View"
          count={minePending}
          countLabel={minePendingLabel}
          divider={false}
        >
          <PieChart
            data={Object.entries(counts).map(([status, value]) => {
              const m = statusMeta('task', status);
              return { name: m.label, value, color: m.colour };
            })}
          />
        </DashboardCard>
        {!solo && (
        <DashboardCard
          title="Team Tasks Chart View"
          count={teamPending}
          countLabel={teamPendingLabel}
          divider={false}
        >
          <PieChart
            data={Object.entries((teamSummary && teamSummary.by_status) || {}).map(([status, value]) => {
              const m = statusMeta('task', status);
              return { name: m.label, value, color: m.colour };
            })}
          />
        </DashboardCard>
        )}
      </div>
    </>
  );
}

function TaskList({ title, tasks, filter, pending, pendingLabel }) {
  return (
    <DashboardCard title={title} count={pending} countLabel={pendingLabel}>
      {tasks.length === 0 && <div className="ty-empty">No tasks yet.</div>}
      {tasks.map((t) => {
        const fullTitle = t.title || '';
        const displayTitle = truncateTaskTitle(fullTitle);
        const dateInfo = taskCardDate(t);
        return (
          <div className="ty-dash-item" key={t.id}>
            <div className="ty-dash-item__top">
              <Link
                to={buildPath.taskDetails(t.id)}
                className="ty-dash-item__title"
                title={fullTitle}
              >
                {displayTitle}
              </Link>
              <span className="ty-dash-item__num" title={`Task #${t.id}`}>
                #{t.id}
              </span>
            </div>
            <div className="ty-dash-item__meta">
              {dateInfo ? (
                <span className="ty-dash-item__date">
                  {dateInfo.kind}: {dateInfo.date}
                </span>
              ) : null}
              {/* Assignee avatars — initials (First + Last name) or the
                  uploaded profile picture for each user the task is
                  assigned to.  Data comes from ``TaskSerializer.assignees``. */}
              <AvatarGroup people={t.assignees || []} size={26} max={3} />
              <StatusBadge domain="task" value={t.status} />
            </div>
          </div>
        );
      })}

      <div style={{ textAlign: 'center', marginTop: 8 }}>
        <Link to={buildPath.tasksList(filter)}>Show all →</Link>
      </div>
    </DashboardCard>
  );
}
